// The framework-free core of the Image component: next/image's own algorithm for
// srcset, sizes, fill and the blur placeholder, pointed at this project's optimizer
// (`GET <api>/image/<width>/<quality>/<source>.webp`, apps/server/src/image.ts). The
// server imports the same widths, qualities and hosts, so a URL built here is always
// one it accepts. Everything is in the path, never the query string: a CDN set to
// ignore query strings (ArvanCloud offers that) would otherwise hand one image to all.

import type { CSSProperties } from "react";

export type ImageConfig = {
  /** absolute URL of the optimizer endpoint, e.g. https://api.tabankhodro.cloud/image */
  path: string;
  deviceSizes: readonly number[];
  imageSizes: readonly number[];
  qualities: readonly number[];
  /** hosts whose images go through the optimizer; anything else is served as is */
  remoteHosts: readonly string[];
  /** turn optimization off everywhere (tests, an optimizer outage) */
  unoptimized: boolean;
};

export const imageConfigDefault: ImageConfig = {
  path: "/image",
  // next/image defaults (Next 16)
  deviceSizes: [640, 750, 828, 1080, 1200, 1920, 2048, 3840],
  imageSizes: [32, 48, 64, 96, 128, 256, 384],
  qualities: [50, 75, 90],
  // only the public bucket: the API server cannot reach most foreign hosts (Unsplash among
  // them), so an image from anywhere else is shown as it is instead of failing in the optimizer
  remoteHosts: ["taban.s3.ir-thr-at1.arvanstorage.ir"],
  unoptimized: false,
};

/** Every width the optimizer renders, ascending */
export const allImageSizes = (config: ImageConfig) =>
  [...config.imageSizes, ...config.deviceSizes].sort((a, b) => a - b);

export type ImageLoaderProps = { src: string; width: number; quality?: number };
export type ImageLoader = (props: ImageLoaderProps) => string;

/** Nearest allowed quality; next/image snaps the same way instead of failing */
export function nearestQuality(quality: number, qualities: readonly number[]) {
  let best = qualities[0] ?? 75;
  for (const candidate of qualities) {
    if (Math.abs(candidate - quality) < Math.abs(best - quality)) best = candidate;
  }
  return best;
}

/** URL-safe base64 of the source's UTF-8 bytes, unpadded: any URL as one plain path segment */
export function encodeImageSource(src: string) {
  let binary = "";
  for (const byte of new TextEncoder().encode(src)) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

/** The source URL back from its path segment; null when the segment is not valid base64url */
export function decodeImageSource(segment: string) {
  if (!/^[A-Za-z0-9_-]+$/.test(segment)) return null;
  try {
    const binary = atob(segment.replaceAll("-", "+").replaceAll("_", "/"));
    return new TextDecoder("utf-8", { fatal: true }).decode(
      Uint8Array.from(binary, (char) => char.charCodeAt(0)),
    );
  } catch {
    return null;
  }
}

export const createImageLoader =
  (config: ImageConfig): ImageLoader =>
  ({ src, width, quality }) =>
    `${config.path}/${width}/${nearestQuality(quality ?? 75, config.qualities)}/${encodeImageSource(src)}.webp`;

/** The smallest width the optimizer renders that covers `width` (it refuses any other) */
export function coveringSize(width: number, config: ImageConfig) {
  const all = allImageSizes(config);
  return all.find((size) => size >= width) ?? all[all.length - 1] ?? width;
}

/** One optimized URL for code that sets `src` itself (a lens, a poster); `src` when not optimizable */
export const optimizedUrl = (src: string, width: number, config: ImageConfig, quality?: number) =>
  isOptimizable(src, config)
    ? createImageLoader(config)({ src, width: coveringSize(width, config), quality })
    : src;

/** Only absolute http(s) images on an allowed host; relative, data:, blob: and SVG pass through */
export function isOptimizable(src: string, config: ImageConfig) {
  if (config.unoptimized || !/^https?:\/\//i.test(src)) return false;
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return false;
  }
  if (url.pathname.toLowerCase().endsWith(".svg")) return false;
  return config.remoteHosts.includes(url.host);
}

/**
 * next/image's getWidths: with `sizes`, every width at or above the smallest vw share
 * of the smallest device; with a fixed width, 1x and 2x (3x screens gain nothing
 * visible for a lot of bytes); with neither, the device sizes.
 */
export function getWidths(
  config: ImageConfig,
  width: number | undefined,
  sizes: string | undefined,
) {
  const all = allImageSizes(config);
  if (sizes) {
    const viewportWidth = /(^|\s)(1?\d?\d)vw/g;
    const percents: number[] = [];
    for (let match = viewportWidth.exec(sizes); match; match = viewportWidth.exec(sizes)) {
      percents.push(Number(match[2]));
    }
    if (percents.length > 0) {
      const smallestRatio = Math.min(...percents) * 0.01;
      const smallestDevice = config.deviceSizes[0] ?? 0;
      return {
        widths: all.filter((size) => size >= smallestDevice * smallestRatio),
        kind: "w" as const,
      };
    }
    return { widths: all, kind: "w" as const };
  }
  if (width === undefined) return { widths: [...config.deviceSizes], kind: "w" as const };
  const widths = [
    ...new Set(
      [width, width * 2].map(
        (target) => all.find((size) => size >= target) ?? all[all.length - 1] ?? target,
      ),
    ),
  ];
  return { widths, kind: "x" as const };
}

export function generateImgAttrs(input: {
  config: ImageConfig;
  src: string;
  unoptimized: boolean;
  width: number | undefined;
  quality: number | undefined;
  sizes: string | undefined;
  loader: ImageLoader;
}) {
  const { config, src, unoptimized, width, quality, sizes, loader } = input;
  if (unoptimized) return { src, srcSet: undefined, sizes: undefined };
  const { widths, kind } = getWidths(config, width, sizes);
  const last = widths[widths.length - 1] ?? width ?? 0;
  return {
    sizes: !sizes && kind === "w" ? "100vw" : sizes,
    srcSet: widths
      .map(
        (w, index) =>
          `${loader({ src, quality, width: w })} ${kind === "w" ? w : index + 1}${kind}`,
      )
      .join(", "),
    // src stays last: Safari starts fetching `src` before `srcSet` and `sizes` are set otherwise
    src: loader({ src, quality, width: last }),
  };
}

/** next/image's blur: a Gaussian-blurred SVG around the tiny placeholder, edges kept opaque */
export function getImageBlurSvg(input: {
  width: number | undefined;
  height: number | undefined;
  blurDataURL: string;
  objectFit: CSSProperties["objectFit"];
}) {
  const std = 20;
  const viewBox = input.width && input.height ? `viewBox='0 0 ${input.width} ${input.height}'` : "";
  const preserveAspectRatio = viewBox
    ? "none"
    : input.objectFit === "contain"
      ? "xMidYMid"
      : input.objectFit === "cover"
        ? "xMidYMid slice"
        : "none";
  return `%3Csvg xmlns='http://www.w3.org/2000/svg' ${viewBox}%3E%3Cfilter id='b' color-interpolation-filters='sRGB'%3E%3CfeGaussianBlur stdDeviation='${std}'/%3E%3CfeColorMatrix values='1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 100 -1' result='s'/%3E%3CfeFlood x='0' y='0' width='100%25' height='100%25'/%3E%3CfeComposite operator='out' in='s'/%3E%3CfeComposite in2='SourceGraphic'/%3E%3CfeGaussianBlur stdDeviation='${std}'/%3E%3C/filter%3E%3Cimage width='100%25' height='100%25' x='0' y='0' preserveAspectRatio='${preserveAspectRatio}' style='filter: url(%23b);' href='${input.blurDataURL}'/%3E%3C/svg%3E`;
}

export type ImagePlaceholder = "empty" | "blur" | `data:image/${string}`;

export type ImageOptions = {
  src: string;
  alt: string;
  width?: number | `${number}`;
  height?: number | `${number}`;
  /** cover the positioned parent instead of taking width × height */
  fill?: boolean;
  sizes?: string;
  quality?: number | `${number}`;
  /** above-the-fold image: eager, high priority and preloaded from the document head */
  preload?: boolean;
  /** next/image's older name for `preload` */
  priority?: boolean;
  loading?: "lazy" | "eager";
  fetchPriority?: "high" | "low" | "auto";
  decoding?: "async" | "sync" | "auto";
  placeholder?: ImagePlaceholder;
  blurDataURL?: string;
  unoptimized?: boolean;
  loader?: ImageLoader;
  /** keep this as the `src` attribute (SEO) while srcset still serves optimized widths */
  overrideSrc?: string;
  className?: string;
  style?: CSSProperties;
};

const toInt = (value: number | `${number}` | undefined) => {
  if (value === undefined) return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number) : undefined;
};

// object-fit values a background can mirror; the rest fall back to cover
const backgroundSizeFor = (objectFit: CSSProperties["objectFit"]) =>
  objectFit === "contain" || objectFit === "cover"
    ? objectFit
    : objectFit === "fill"
      ? "100% 100%"
      : "cover";

/**
 * next/image's getImgProps: the <img> attributes for these options. `blurComplete`
 * drops the placeholder once the real image has decoded; `showAltText` makes the alt
 * text visible after a load error (it is transparent while loading).
 */
export function getImgProps(
  options: ImageOptions,
  state: { config: ImageConfig; blurComplete: boolean; showAltText: boolean },
) {
  const { config } = state;
  const preload = Boolean(options.preload || options.priority);
  let unoptimized = options.unoptimized ?? false;
  let lazy = !preload && (options.loading === "lazy" || options.loading === undefined);
  const src = options.src;
  if (!src || src.startsWith("data:") || src.startsWith("blob:")) {
    unoptimized = true;
    lazy = false;
  }
  if (!options.loader && !isOptimizable(src, config)) unoptimized = true;

  const width = toInt(options.width);
  const height = toInt(options.height);
  const quality = toInt(options.quality);
  const fill = options.fill ?? false;
  const placeholder = options.placeholder ?? "empty";

  const imgStyle: CSSProperties = {
    ...(fill ? { position: "absolute", height: "100%", width: "100%", inset: 0 } : undefined),
    ...(state.showAltText ? undefined : { color: "transparent" }),
    ...options.style,
  };
  const backgroundImage =
    state.blurComplete || placeholder === "empty"
      ? null
      : placeholder === "blur"
        ? `url("data:image/svg+xml;charset=utf-8,${getImageBlurSvg({
            width,
            height,
            blurDataURL: options.blurDataURL ?? "",
            objectFit: imgStyle.objectFit,
          })}")`
        : `url("${placeholder}")`;
  const placeholderStyle: CSSProperties = backgroundImage
    ? {
        backgroundSize: backgroundSizeFor(imgStyle.objectFit),
        backgroundPosition: imgStyle.objectPosition ?? "50% 50%",
        backgroundRepeat: "no-repeat",
        backgroundImage,
      }
    : {};

  const attrs = generateImgAttrs({
    config,
    src,
    unoptimized,
    width,
    quality,
    sizes: options.sizes,
    loader: options.loader ?? createImageLoader(config),
  });

  return {
    props: {
      alt: options.alt,
      loading: lazy ? ("lazy" as const) : options.loading,
      fetchPriority: preload ? "high" : options.fetchPriority,
      width,
      height,
      decoding: options.decoding ?? "async",
      className: options.className,
      style: { ...imgStyle, ...placeholderStyle },
      sizes: attrs.sizes,
      srcSet: attrs.srcSet,
      src: options.overrideSrc ?? attrs.src,
    },
    meta: { unoptimized, preload, placeholder, fill },
  };
}
